import React, { useState, useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { ChessSocialContext } from '../../util/ChessSocialContext';
import { cleanProfanity } from '../../util/filterProfanity';
import { fetchGame } from '../../util/api/game';
import { firestore } from '../../firebase';
import { collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import axios from 'axios';
import chessslogo from "../../images/chessslogo.png";
import { broadcastNotificationStatusChange } from '../../util/notificationEvents';

// Import shared hooks and components
import { useNotifications } from '../../hooks/useNotifications';
import { useNotificationStatus } from '../../hooks/useNotificationStatus';
import GameSummaryMobile from '../components/GameSummaryMobile';
import { isNativeApp } from '../../util/platformDetection';

const MobileInteractions = (props) => {
  const { user } = props;
  
  // Mobile-specific state
  const [activeTab, setActiveTab] = useState('notifications');
  const [isMobileView, setIsMobileView] = useState(true);
  const [previousTab, setPreviousTab] = useState(null);

  // Use notification hooks
  const {
    newFollowers,
    newComments,
    notifications,
    loading,
    error,
    hasNewFollowers,
    hasNewComments,
    hasNewLikes,
    getCommentsGroupedByGame,
    refetchNotifications,
    setNewFollowers,
    setNewComments,
    setNotifications
  } = useNotifications(user?.credentials?.handle);

  const {
    handleTabSwitch,
    useVisibilityChangeListener,
    markFollowersAsReadLocally,
    markCommentsAsReadLocally,
    markLikesAsReadLocally,
    markFollowersAsReadInFirestore,
    markCommentsAsReadInFirestore,
    markLikesAsReadInFirestore,
    hasUnreadNotifications
  } = useNotificationStatus();

  // Set up visibility change listener for mobile background/foreground
  useVisibilityChangeListener(activeTab, newFollowers, newComments, notifications.likesByGameId);

  // Auto-clear comment and like notifications 3 seconds after page load
  useEffect(() => {
    // Only start timer if user data is loaded
    if (!user?.credentials?.handle) {
      // console.log("Waiting for user data to load...");
      return;
    }
    
    // console.log("User loaded, starting 3 second timer to mark notifications as read");
    const timer = setTimeout(async () => {
      const authenticatedUserHandle = user.credentials.handle;
      // console.log("Timer fired. User handle:", authenticatedUserHandle);
      
      try {
        // Get all unread notifications for this user
        const q = query(
          collection(firestore, "notifications"),
          where("targetUser", "==", authenticatedUserHandle),
          where("status", "==", "unread"),
          where("type", "in", ["new_comment", "new_like"])
        );
        
        const snapshot = await getDocs(q);
        // console.log("Found", snapshot.size, "unread notifications to mark as read");
        
        // Update each one to read
        const updatePromises = [];
        snapshot.forEach((doc) => {
          updatePromises.push(
            updateDoc(doc.ref, { status: "read" })
          );
        });
        
        await Promise.all(updatePromises);
        // console.log("Marked all notifications as read");
        
        // Clear the bottom navbar dot
        broadcastNotificationStatusChange(false);
      } catch (error) {
        console.error("Error marking notifications as read:", error);
      }
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [user?.credentials?.handle]); // Run when user data loads

  // Handle tab changes with read status updates
  const handleTabChange = async (newTab) => {
    // Set the new tab first
    setPreviousTab(activeTab);
    setActiveTab(newTab);
    
    // Mark notifications as read when ENTERING the relevant tab
    if (newTab === 'follows') {
      // Mark followers as read when entering follows tab
      await markFollowersAsReadInFirestore(newFollowers);
      markFollowersAsReadLocally(newFollowers, setNewFollowers);
    } else if (newTab === 'notifications') {
      // Mark comments and likes as read when entering notifications tab
      await markCommentsAsReadInFirestore(newComments);
      await markLikesAsReadInFirestore(notifications.likesByGameId);
      markCommentsAsReadLocally(newComments, setNewComments);
      markLikesAsReadLocally(notifications.likesByGameId, setNotifications);
    }
    
    // Broadcast notification status change after updates
    setTimeout(() => {
      const stillHasUnread = hasUnreadNotifications(newFollowers, newComments, notifications.likesByGameId);
      broadcastNotificationStatusChange(stillHasUnread);
    }, 100);
  };

  // Render notifications tab content
  const renderNotifications = () => {
    const commentsGroupedByGame = getCommentsGroupedByGame();
    const gameIds = Object.keys(commentsGroupedByGame);

    if (gameIds.length === 0 && (!notifications.likesByGameId || Object.keys(notifications.likesByGameId).length === 0)) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: '#666',
          textAlign: 'center'
        }}>
          <i className="fa-solid fa-bell" style={{ fontSize: '48px', marginBottom: '20px', color: '#666' }}></i>
          <h3 style={{ color: '#888', marginBottom: '10px' }}>No Notifications</h3>
          <p style={{ color: '#666', fontSize: '14px', maxWidth: '250px' }}>
            When people interact with your games, you'll see notifications here.
          </p>
        </div>
      );
    }

    // Combine all gameIds from comments and likes
    const allGameIds = new Set([
      ...gameIds,
      ...Object.keys(notifications.likesByGameId || {})
    ]);

    // Sort games by most recent notification (newest first)
    const sortedGameIds = Array.from(allGameIds).sort((gameIdA, gameIdB) => {
      const commentsA = commentsGroupedByGame[gameIdA] || [];
      const commentsB = commentsGroupedByGame[gameIdB] || [];
      const likesA = notifications.likesByGameId?.[gameIdA] || [];
      const likesB = notifications.likesByGameId?.[gameIdB] || [];
      
      // Get most recent timestamp for game A
      let mostRecentA = 0;
      [...commentsA, ...likesA].forEach(item => {
        if (item.timestamp) {
          const time = item.timestamp.toDate ? item.timestamp.toDate().getTime() : new Date(item.timestamp).getTime();
          mostRecentA = Math.max(mostRecentA, time);
        }
      });
      
      // Get most recent timestamp for game B
      let mostRecentB = 0;
      [...commentsB, ...likesB].forEach(item => {
        if (item.timestamp) {
          const time = item.timestamp.toDate ? item.timestamp.toDate().getTime() : new Date(item.timestamp).getTime();
          mostRecentB = Math.max(mostRecentB, time);
        }
      });
      
      return mostRecentB - mostRecentA; // Newest first
    });

    return (
      <div style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)' }}>
        {sortedGameIds.map(gameId => {
          const gameComments = commentsGroupedByGame[gameId] || [];
          const gameLikes = notifications.likesByGameId?.[gameId] || [];
          
          // Skip placeholder entries
          if (gameComments.length === 1 && gameComments[0].isPlaceholder && gameLikes.length === 0) {
            return null;
          }

          // Sort comments within this game by newest first
          const sortedGameComments = [...gameComments].sort((a, b) => {
            const timeA = a.timestamp ? (a.timestamp.toDate ? a.timestamp.toDate().getTime() : new Date(a.timestamp).getTime()) : 0;
            const timeB = b.timestamp ? (b.timestamp.toDate ? b.timestamp.toDate().getTime() : new Date(b.timestamp).getTime()) : 0;
            return timeB - timeA; // Newest first
          });

          // Sort likes within this game by newest first
          const sortedGameLikes = [...gameLikes].sort((a, b) => {
            const timeA = a.timestamp ? (a.timestamp.toDate ? a.timestamp.toDate().getTime() : new Date(a.timestamp).getTime()) : 0;
            const timeB = b.timestamp ? (b.timestamp.toDate ? b.timestamp.toDate().getTime() : new Date(b.timestamp).getTime()) : 0;
            return timeB - timeA; // Newest first
          });

          return (
            <GameNotificationCard 
              key={gameId}
              gameId={gameId}
              gameComments={sortedGameComments}
              gameLikes={sortedGameLikes}
            />
          );
        })}
      </div>
    );
  };

  // Component for individual game notification cards  
  const GameNotificationCard = ({ gameId, gameComments, gameLikes }) => {
    const [gameData, setGameData] = useState(null);
    const [gameExists, setGameExists] = useState(true);
    const [gameLoading, setGameLoading] = useState(true);

    useEffect(() => {
      // Check if game exists using the same logic as web version
      const checkGame = async () => {
        try {
          const gameDataResult = await fetchGame(gameId);
          if (gameDataResult && gameDataResult.error && gameDataResult.isNotFound) {
            setGameExists(false);
          } else if (gameDataResult && !gameDataResult.error) {
            setGameExists(true);
            setGameData(gameDataResult);
          }
        } catch (error) {
          console.error('Error checking game:', error);
          setGameExists(false);
        } finally {
          setGameLoading(false);
        }
      };

      checkGame();
    }, [gameId]);

    // Don't render if game doesn't exist
    if (!gameExists) {
      return null;
    }

    // Show loading placeholder
    if (gameLoading) {
      return (
        <div style={{
          marginBottom: '20px',
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          padding: '15px',
          border: '1px solid #333',
          color: '#666',
          textAlign: 'center'
        }}>
          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '20px' }}></i>
        </div>
      );
    }

    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        padding: '15px',
        border: '1px solid #333'
      }}>
        {/* Game Preview */}
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '5px' }}>
          <GameSummaryMobile 
            id={gameId}
            boardWidth={120}
            showDetails={false}
            cornerRadius="4px"
          />
          <div style={{ marginLeft: '15px', flex: 1 }}>
            {/* Game Title */}
            <div style={{ 
              color: 'white', 
              fontSize: '14px', 
              fontWeight: 'bold',
              marginBottom: '5px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '200px'
            }}>
              {cleanProfanity(gameData?.caption) || 'Untitled Game'}
            </div>
            
            {/* Likes Summary */}
            {gameLikes.length > 0 && (
              <div style={{
                color: 'gold',
                fontSize: '12px',
                marginBottom: '5px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {gameLikes.length === 1 
                  ? `${gameLikes[0].likedBy} liked your post`
                  : `${gameLikes.length} likes from ${gameLikes.slice(0, 2).map(l => l.likedBy).join(', ')}${gameLikes.length > 2 ? ` and ${gameLikes.length - 2} others` : ''}`
                }
                {gameLikes.some(like => like.status === 'unread') && (
                  <div style={{
                    width: '6px',
                    height: '6px',
                    backgroundColor: '#ff4444',
                    borderRadius: '50%',
                    marginLeft: '8px'
                  }} />
                )}
              </div>
            )}

            {/* Comments with inline display */}
            {gameComments.length > 0 && !gameComments[0].isPlaceholder && (
              <div>
                {gameComments.slice(0, 2).map(comment => (
                  <div key={comment.id} style={{
                    color: '#888',
                    fontSize: '12px',
                    marginBottom: '3px',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}>
                    <div style={{ flex: 1, lineHeight: '1.3' }}>
                      <span style={{ color: 'gold', fontWeight: 'bold' }}>
                        {comment.commentBy}
                      </span>
                      <span style={{ color: '#ccc', marginLeft: '5px' }}>
                        "{cleanProfanity(comment.commentContent || comment.comment)}"
                      </span>
                    </div>
                    {comment.status === 'unread' && (
                      <div style={{
                        width: '6px',
                        height: '6px',
                        backgroundColor: '#ff4444',
                        borderRadius: '50%',
                        marginLeft: '8px',
                        marginTop: '2px',
                        flexShrink: 0
                      }} />
                    )}
                  </div>
                ))}
                {gameComments.length > 2 && (
                  <div style={{
                    color: '#666',
                    fontSize: '11px',
                    marginTop: '2px'
                  }}>
                    +{gameComments.length - 2} more comments
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Component for individual follower cards with profile picture fetching
  const FollowerCard = ({ follower }) => {
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchProfileData = async () => {
        try {
          const response = await axios.get(`/user/${follower.followedBy}`);
          if (response.data && response.data.user) {
            setProfileData(response.data.user);
          }
        } catch (error) {
          console.error('Error fetching profile data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchProfileData();
    }, [follower.followedBy]);

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '15px',
        marginBottom: '10px',
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        border: '1px solid #333',
        position: 'relative'
      }}>
        {/* Profile Picture */}
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: '#333',
          marginRight: '12px',
          overflow: 'hidden',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {loading ? (
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '16px', color: '#666' }}></i>
          ) : (
            <img
              src={profileData && profileData.photoUrl ? profileData.photoUrl : chessslogo}
              alt={`${follower.followedBy}'s profile`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          )}
        </div>
        
        {/* Follower Info */}
        <div style={{ flex: 1 }}>
          <div style={{
            color: 'gold',
            fontSize: '14px',
            fontWeight: 'bold',
            marginBottom: '2px'
          }}>
            {follower.followedBy}
          </div>
          <div style={{
            color: '#888',
            fontSize: '12px'
          }}>
            Started following you
          </div>
        </div>

        {/* Unread indicator */}
        {follower.status === 'unread' && (
          <div style={{
            width: '8px',
            height: '8px',
            backgroundColor: '#ff4444',
            borderRadius: '50%',
            position: 'absolute',
            top: '10px',
            right: '10px'
          }} />
        )}

        {/* Follow Back Button */}
        <Link 
          to={{
            pathname: `/profile/${follower.followedBy}`,
            state: {
              returnTo: '/interaction',
              returnLabel: 'Back to Interactions',
              from: 'interactions'
            }
          }}
          style={{
            padding: '6px 12px',
            backgroundColor: '#333',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            transition: 'background-color 0.2s'
          }}
        >
          View Profile
        </Link>
      </div>
    );
  };

  // Render follows tab content
  const renderFollows = () => {
    if (newFollowers.length === 0) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: '#666',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>👥</div>
          <h3 style={{ color: '#888', marginBottom: '10px' }}>No New Followers</h3>
          <p style={{ color: '#666', fontSize: '14px', maxWidth: '250px' }}>
            When people follow you, you'll see them here.
          </p>
        </div>
      );
    }

    return (
      <div style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)' }}>
        {newFollowers.map(follower => (
          <FollowerCard key={follower.id} follower={follower} />
        ))}
      </div>
    );
  };

  // Render mobile-optimized UI
  const renderMobileContent = () => {
    return (
      <div style={{
        width: '100vw',
        height: 'calc(100dvh - 55px)', // Account for bottom navigation
        backgroundColor: '#1a1a1a',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        paddingTop: 'env(safe-area-inset-top)', // Header only in native app
        paddingBottom: 'env(safe-area-inset-bottom)' // iPhone gesture area
      }}>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          backgroundColor: '#1a1a1a',
          borderBottom: '1px solid #333'
        }}>
          <button
            onClick={() => handleTabChange('notifications')}
            style={{
              flex: 1,
              padding: '10px',
              border: 'none',
              backgroundColor: activeTab === 'notifications' ? '#333' : 'transparent',
              borderBottom: activeTab === 'notifications' ? '3px solid gold' : 'none',
              color: activeTab === 'notifications' ? 'gold' : '#888',
              fontSize: '16px',
              fontWeight: 'bold',
              position: 'relative'
            }}
          >
            Notifications
            {(hasNewComments() || hasNewLikes()) && (
              <div style={{
                position: 'absolute',
                top: '4px',
                right: '8px',
                width: '8px',
                height: '8px',
                backgroundColor: '#ff4444',
                borderRadius: '50%'
              }} />
            )}
          </button>
          <button
            onClick={() => handleTabChange('follows')}
            style={{
              flex: 1,
              padding: '10px',
              border: 'none',
              backgroundColor: activeTab === 'follows' ? '#333' : 'transparent',
              borderBottom: activeTab === 'follows' ? '3px solid gold' : 'none',
              color: activeTab === 'follows' ? 'gold' : '#888',
              fontSize: '16px',
              fontWeight: 'bold',
              position: 'relative'
            }}
          >
            Following
            {hasNewFollowers() && (
              <div style={{
                position: 'absolute',
                top: '4px',
                right: '8px',
                width: '8px',
                height: '8px',
                backgroundColor: '#ff4444',
                borderRadius: '50%'
              }} />
            )}
          </button>
        </div>

        {/* Content Area */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: '10px'
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#666'
            }}>
              <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '24px' }}></i>
            </div>
          ) : error ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#ff4444',
              textAlign: 'center'
            }}>
              <i className="fa-solid fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
              <div>Error loading notifications</div>
              <button 
                onClick={refetchNotifications}
                style={{
                  marginTop: '10px',
                  padding: '8px 16px',
                  backgroundColor: '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {activeTab === 'notifications' && renderNotifications()}
              {activeTab === 'follows' && renderFollows()}
            </>
          )}
        </div>
      </div>
    );
  };

  return renderMobileContent();
};

const mapStateToProps = (state) => ({
  user: state.user,
  data: state.data
});

export default connect(mapStateToProps)(MobileInteractions);